<template>
  <div
    ref="messagesContainer"
    class="kb-chat-log"
    role="log"
    aria-live="polite"
    aria-label="Conversation"
  >
    <!-- Nothing said yet: who this is and a few questions to start with -->
    <div
      v-if="!hasMessages && !isLoading"
      class="grid gap-4"
    >
      <p class="kb-chat-hello">
        The answer to life, the universe, and your Krewebacchus questions.
      </p>
      <div class="grid gap-1">
        <p class="kb-label m-0">
          Try asking
        </p>
        <ul class="kb-rows list-none m-0 p-0">
          <li
            v-for="suggestion in suggestions"
            :key="suggestion"
          >
            <button
              type="button"
              class="kb-chat-suggestion"
              @click="sendSuggestion(suggestion)"
            >
              <span>{{ suggestion }}</span>
              <i
                class="pi pi-arrow-right"
                aria-hidden="true"
              />
            </button>
          </li>
        </ul>
      </div>
    </div>

    <template v-else>
      <div
        v-for="message in messages"
        :key="message.id || message.timestamp.getTime()"
        class="kb-chat-msg"
        :class="`is-${message.role}`"
      >
        <p class="kb-chat-who">
          <span>{{ message.role === 'user' ? 'you' : 'deep thought' }}</span>
          <time :datetime="message.timestamp.toISOString()">{{ formatTime(message.timestamp) }}</time>
        </p>
        <!-- eslint-disable vue/no-v-html -- escaped and scheme-restricted by formatChatMessage; see utils/chatMessageFormat.ts -->
        <div
          class="kb-chat-text"
          v-html="formatChatMessage(message.content)"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </template>

    <div
      v-if="isLoading"
      class="kb-chat-msg is-assistant"
    >
      <p class="kb-chat-who">
        <span>deep thought</span>
      </p>
      <p
        class="kb-chat-text kb-chat-thinking"
        role="status"
      >
        {{ loadingStatus || 'thinking' }}<span
          class="kb-caret"
          aria-hidden="true"
        />
      </p>
    </div>

    <p
      v-if="error"
      class="kb-callout-alert m-0"
      role="alert"
    >
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { nextTick, watch } from 'vue'
import { formatChatMessage } from '~/utils/chatMessageFormat'

const { messages, isLoading, loadingStatus, error, hasMessages, sendMessage } = useChatbot()

const messagesContainer = ref<HTMLElement | null>(null)

const suggestions = [
  'How do I pay my dues?',
  'How do I join a SubKrewe?',
  'What are Red Shirts?',
]

function sendSuggestion(text: string) {
  sendMessage(text)
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function scrollToEnd() {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

// Follow the conversation: new messages and the thinking line
watch(() => messages.value.length, async () => {
  await nextTick()
  scrollToEnd()
})

watch(isLoading, async () => {
  await nextTick()
  scrollToEnd()
})
</script>

<style scoped>
/* The conversation as a console transcript: who and when in mono, then
   what was said. Deep Thought's answers hang off a green rule; your own
   lines sit in a raised box */
.kb-chat-log {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1.125rem;
  padding: 1rem;
  overflow-y: auto;
  background: var(--kb-bg);
}

.kb-chat-hello {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--kb-muted);
}

.kb-chat-suggestion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  min-height: 2.75rem;
  padding: 0.5rem 0.25rem;
  border: 0;
  background: none;
  font: inherit;
  font-size: 0.875rem;
  color: var(--kb-fg);
  text-align: left;
  cursor: pointer;
}

.kb-chat-suggestion .pi {
  font-size: 0.75rem;
  color: var(--kb-dim);
}

.kb-chat-suggestion:hover {
  background: var(--kb-hover);
}

.kb-chat-suggestion:hover .pi {
  color: var(--kb-accent);
}

.kb-chat-suggestion:focus-visible {
  outline: 2px solid var(--kb-accent);
  outline-offset: -2px;
}

.kb-chat-msg {
  display: grid;
  gap: 0.25rem;
}

.kb-chat-who {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin: 0;
  font: 500 11px var(--kb-font-mono);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--kb-muted);
}

.kb-chat-msg.is-assistant .kb-chat-who {
  color: var(--kb-accent);
}

.kb-chat-who time {
  letter-spacing: 0;
  text-transform: none;
  color: var(--kb-dim);
}

.kb-chat-text {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: var(--kb-fg);
  overflow-wrap: anywhere;
}

.kb-chat-msg.is-assistant .kb-chat-text {
  padding-left: 0.75rem;
  border-left: 2px solid var(--kb-accent);
}

.kb-chat-msg.is-user .kb-chat-text {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--kb-line);
  border-radius: 2px;
  background: var(--kb-raised);
}

.kb-chat-text :deep(a) {
  color: var(--kb-accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.kb-chat-thinking {
  font-family: var(--kb-font-mono);
  font-size: 0.8125rem;
  color: var(--kb-muted);
}
</style>
